/**
 * SectionCard — 替代裸用 antd Card 的通用區塊容器（v3 "Quiet Refinement"）
 * ──────────────────────────────────────────────
 * 升級亮點：
 *   - 1px border（var(--border-subtle)）替代 shadow-by-default，更輕盈
 *   - radius = 8px（md），稍大更現代
 *   - tone prop：default | accent | warning | success | danger — 邊框語義著色
 *   - subtle variant：輕量版（淡邊框 + 更小 padding），用於次要信息
 *   - 可折疊（collapsible）
 *   - hover 抬起效果（hoverable）
 */
import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import {
  color,
  font,
  space,
  text,
  weight,
  leading,
  tracking,
  radius,
  shadow,
} from '@/styles/tokens';

type Tone = 'default' | 'accent' | 'warning' | 'success' | 'danger' | 'info';
type Variant = 'default' | 'subtle';

/**
 * tone 對應的視覺映射：
 *   - borderColor：卡片邊框顏色
 *   - headerBg：header 行背景（accent 時有輕微着色）
 *   - indicatorColor：左上角 3px 彩色角標（僅 accent/warning/success/danger）
 */
const TONE_MAP: Record<Tone, { border: string; headerBg: string; indicator: string | null }> = {
  default: {
    border: color.border.subtle,
    headerBg: 'transparent',
    indicator: null,
  },
  accent: {
    border: 'rgba(26, 133, 73, 0.28)' /* forest @ 28% */,
    headerBg: 'rgba(26, 133, 73, 0.04)',
    indicator: color.brand.primary,
  },
  success: {
    border: 'rgba(5, 150, 105, 0.28)',
    headerBg: 'rgba(5, 150, 105, 0.04)',
    indicator: color.state.success,
  },
  warning: {
    border: 'rgba(217, 119, 6, 0.30)',
    headerBg: 'rgba(217, 119, 6, 0.04)',
    indicator: color.state.warning,
  },
  danger: {
    border: 'rgba(225, 29, 72, 0.28)',
    headerBg: 'rgba(225, 29, 72, 0.04)',
    indicator: color.state.danger,
  },
  info: {
    border: 'rgba(2, 132, 199, 0.24)',
    headerBg: 'rgba(2, 132, 199, 0.04)',
    indicator: color.state.info,
  },
};

export interface SectionCardProps {
  /** 區塊標題 */
  title?: ReactNode;
  /** 區塊副標題 */
  description?: ReactNode;
  /** 標題前圖標 */
  icon?: ReactNode;
  /** 右上操作區 */
  actions?: ReactNode;
  /** 主內容 */
  children?: ReactNode;
  /** 底部操作/補充行 */
  footer?: ReactNode;
  /** 是否取消邊框（用於嵌套於 Card 內） */
  borderless?: boolean;
  /** 是否取消預設 padding（用於 Table 等自帶 padding 的內容） */
  padless?: boolean;
  /** 視覺變體：default（標準）/ subtle（輕量，用於次要信息） */
  variant?: Variant;
  /** 語義色調（影響邊框 + header 背景） */
  tone?: Tone;
  /** 自定義 className */
  className?: string;
  /** 是否 hover 高亮 */
  hoverable?: boolean;
  /** 是否可折疊 */
  collapsible?: boolean;
  /** 折疊初始狀態（配合 collapsible） */
  defaultCollapsed?: boolean;
  /** 點擊事件 */
  onClick?: () => void;
}

export default function SectionCard({
  title,
  description,
  icon,
  actions,
  children,
  footer,
  borderless = false,
  padless = false,
  variant = 'default',
  tone = 'default',
  className,
  hoverable,
  collapsible = false,
  defaultCollapsed = false,
  onClick,
}: SectionCardProps) {
  const hasHeader = !!(title || actions || icon);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const palette = TONE_MAP[tone];

  /* variant=subtle：邊框更淡、padding 稍小 */
  const isSubtle = variant === 'subtle';
  const bodyPadding = isSubtle ? space[4] : space[5];

  return (
    <div
      className={className}
      onClick={onClick}
      style={{
        background: color.surface.base,
        border: borderless ? 'none' : `1px solid ${palette.border}`,
        borderRadius: radius.md,
        /* subtle variant 用更輕的陰影 */
        boxShadow: borderless ? 'none' : isSubtle ? 'none' : shadow.xs,
        overflow: 'hidden',
        cursor: onClick || hoverable ? 'pointer' : undefined,
        transition:
          hoverable || onClick
            ? 'box-shadow 200ms cubic-bezier(0.16, 1, 0.3, 1), border-color 200ms cubic-bezier(0.16, 1, 0.3, 1), transform 200ms cubic-bezier(0.16, 1, 0.3, 1)'
            : undefined,
        /* tone 指示器：左側 3px 彩色竪條 */
        borderLeft: !borderless && palette.indicator ? `3px solid ${palette.indicator}` : undefined,
      }}
      onMouseEnter={(e) => {
        if (!hoverable && !onClick) return;
        e.currentTarget.style.boxShadow = shadow.md;
        e.currentTarget.style.borderColor = color.border.default;
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        if (!hoverable && !onClick) return;
        e.currentTarget.style.boxShadow = shadow.xs;
        e.currentTarget.style.borderColor = palette.border;
        e.currentTarget.style.transform = 'translateY(0)';
      }}
    >
      {hasHeader && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: isSubtle ? '10px 16px' : '14px 20px',
            borderBottom: collapsed ? 'none' : `1px solid ${color.border.subtle}`,
            gap: space[3],
            minHeight: isSubtle ? 44 : 52,
            background: palette.headerBg,
            cursor: collapsible ? 'pointer' : undefined,
          }}
          onClick={
            collapsible
              ? (e) => {
                  e.stopPropagation();
                  setCollapsed((v) => !v);
                }
              : undefined
          }
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: space[3], minWidth: 0 }}>
            {/* icon chip */}
            {icon && (
              <span
                style={{
                  display: 'inline-flex',
                  width: 28,
                  height: 28,
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: radius.md,
                  background: color.brand.primarySoft,
                  color: color.brand.primary,
                  flexShrink: 0,
                }}
              >
                {icon}
              </span>
            )}
            <div style={{ minWidth: 0 }}>
              {title && (
                <div
                  style={{
                    fontFamily: font.display,
                    fontSize: isSubtle ? text.base : text.md,
                    fontWeight: weight.semibold,
                    color: color.text.primary,
                    letterSpacing: tracking.tight,
                    lineHeight: leading.tight,
                  }}
                >
                  {title}
                </div>
              )}
              {description && (
                <div
                  style={{
                    fontSize: text.sm,
                    color: color.text.tertiary,
                    marginTop: 2,
                    lineHeight: leading.snug,
                  }}
                >
                  {description}
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: space[2],
              flexShrink: 0,
            }}
          >
            {actions}
            {/* 折疊箭頭 */}
            {collapsible && (
              <span
                style={{
                  display: 'inline-flex',
                  color: color.text.muted,
                  transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                  transition: 'transform 200ms cubic-bezier(0.16, 1, 0.3, 1)',
                }}
              >
                <ChevronDown size={16} />
              </span>
            )}
          </div>
        </div>
      )}

      {/* 內容區（折疊時隱藏） */}
      {!collapsed && children !== undefined && (
        <div style={{ padding: padless ? 0 : bodyPadding }}>{children}</div>
      )}

      {/* footer */}
      {!collapsed && footer && (
        <div
          style={{
            padding: isSubtle ? '10px 16px' : '12px 20px',
            borderTop: `1px solid ${color.border.subtle}`,
            background: color.surface.muted,
            fontSize: text.sm,
            color: color.text.tertiary,
          }}
        >
          {footer}
        </div>
      )}
    </div>
  );
}
